import { suinsRepository } from '@/repositories';
import { ensureSuiClient } from '@/utils/sui-compat';
import { useSuiClient } from '@mysten/dapp-kit';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

export const SUINS_CACHE_TIME_MS = 6 * 60 * 60 * 1000;

export const suinsQueryKeys = {
	name: (address: string) => ['suins', 'name', address],
	address: (name: string) => ['suins', 'address', name],
} as const;

const normalize = (value?: string | null) => value?.trim().toLowerCase() ?? '';

export const useResolvedSuinsName = (address?: string | null, options?: { enabled?: boolean }) => {
	const client = ensureSuiClient(useSuiClient());
	const normalized = normalize(address);

	return useQuery({
		queryKey: suinsQueryKeys.name(normalized),
		queryFn: async () => {
			if (!normalized) return null;
			const resolved = await suinsRepository.resolveName(client, normalized);
			return resolved || null;
		},
		enabled: Boolean(options?.enabled ?? true) && Boolean(normalized),
		staleTime: SUINS_CACHE_TIME_MS,
		gcTime: SUINS_CACHE_TIME_MS,
		refetchInterval: false,
		refetchOnWindowFocus: false,
	});
};

export const useResolvedSuinsAddress = (name?: string | null, options?: { enabled?: boolean }) => {
	const client = ensureSuiClient(useSuiClient());
	const normalized = normalize(name);

	return useQuery({
		queryKey: suinsQueryKeys.address(normalized),
		queryFn: async () => {
			if (!normalized) return null;
			const resolved = await suinsRepository.resolveAddress(client, normalized);
			return resolved || null;
		},
		enabled: Boolean(options?.enabled ?? true) && Boolean(normalized),
		staleTime: SUINS_CACHE_TIME_MS,
		gcTime: SUINS_CACHE_TIME_MS,
		refetchInterval: false,
		refetchOnWindowFocus: false,
	});
};

export const useSuinsResolver = () => {
	const client = ensureSuiClient(useSuiClient());
	const queryClient = useQueryClient();

	const resolveName = useCallback(
		async (address?: string | null) => {
			const normalized = normalize(address);
			if (!normalized) return null;

			return queryClient.fetchQuery({
				queryKey: suinsQueryKeys.name(normalized),
				queryFn: async () => {
					const resolved = await suinsRepository.resolveName(client, normalized);
					return resolved || null;
				},
				staleTime: SUINS_CACHE_TIME_MS,
				gcTime: SUINS_CACHE_TIME_MS,
			});
		},
		[client, queryClient],
	);

	const resolveAddress = useCallback(
		async (name?: string | null) => {
			const normalized = normalize(name);
			if (!normalized) return null;

			return queryClient.fetchQuery({
				queryKey: suinsQueryKeys.address(normalized),
				queryFn: async () => {
					const resolved = await suinsRepository.resolveAddress(client, normalized);
					return resolved || null;
				},
				staleTime: SUINS_CACHE_TIME_MS,
				gcTime: SUINS_CACHE_TIME_MS,
			});
		},
		[client, queryClient],
	);

	return { resolveName, resolveAddress };
};
